import { formatCurrency, pluralize } from "@sellar-io/common";
import { CheckoutOrderItem } from "./types";
import { Icon, OrderSummaryPrice } from "../../../../components";
import { ImageWithFallback } from "../../../../components/image/imageWithFallback";
import { twMerge } from "tailwind-merge";
import { useCheckoutOrderSummary } from "./useCheckoutOrderSummary";
import { Disclosure, DisclosureButton } from "@headlessui/react";
import { ReactNode } from "react";
import { IconType } from "@sellar-io/components";
import { useIsDesktop } from "./useIsDesktop";
import { Alert } from "../../../../components/alert/Alert";
import { isPromotionalCheckoutOrderItem } from "../../categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";
import type { PromotionListResponseType } from "@sellar-io/api-v2/types";
import { DietaryDisplayList } from "../../../../components/dietaryDisplayList/DietaryDisplayList";
import { SlideTransition } from "../../../../components/transition/SlideTransition";

type CheckoutOrderSummaryProps = {
  orderItemList: CheckoutOrderItem[];
  outOfStockProductIds: number[];
  productIdsWithPricingChange: number[];
  activeProductIdList: number[];
  orderSummary: ReturnType<typeof useCheckoutOrderSummary>;
  promotions: PromotionListResponseType;
  showSupplierName?: boolean;
};

const getOrderItemCount = (orderItemList: CheckoutOrderItem[]) =>
  orderItemList.reduce((count, item) => count + item.quantity, 0);

export const CheckoutOrderSummary = ({
  orderItemList,
  orderSummary,
  outOfStockProductIds,
  productIdsWithPricingChange,
  activeProductIdList,
  promotions,
  showSupplierName = false,
}: CheckoutOrderSummaryProps) => {
  const { isDesktop } = useIsDesktop();

  if (!isDesktop) {
    return (
      <CheckoutOrderSummaryMobile
        orderItemList={orderItemList}
        orderSummary={orderSummary}
        outOfStockProductIds={outOfStockProductIds}
        productIdsWithPricingChange={productIdsWithPricingChange}
        activeProductIdList={activeProductIdList}
        promotions={promotions}
        showSupplierName={showSupplierName}
      />
    );
  }

  return (
    <CheckoutOrderSummaryDesktop
      orderItemList={orderItemList}
      orderSummary={orderSummary}
      outOfStockProductIds={outOfStockProductIds}
      productIdsWithPricingChange={productIdsWithPricingChange}
      activeProductIdList={activeProductIdList}
      promotions={promotions}
      showSupplierName={showSupplierName}
    />
  );
};

const CheckoutOrderSummaryMobile = ({
  orderItemList,
  orderSummary,
  outOfStockProductIds,
  productIdsWithPricingChange,
  activeProductIdList,
  promotions,
  showSupplierName,
}: CheckoutOrderSummaryProps) => {
  const itemCount = getOrderItemCount(orderItemList);
  const nonPromotionalOrderItems = orderItemList.filter(
    (orderItem) => !isPromotionalCheckoutOrderItem(orderItem),
  );
  const promotionalOrderItems = orderItemList.filter((orderItem) =>
    isPromotionalCheckoutOrderItem(orderItem),
  );

  return (
    <Disclosure as="div">
      {({ open }) => {
        const iconToggleType: IconType = open ? "CHEVRON_UP" : "CHEVRON_DOWN";
        const viewText = open ? "View less" : "View more";

        return (
          <>
            <div
              className={twMerge(
                "flex flex-col gap-4 rounded-t-lg bg-gray-100 p-4",
                !open && "rounded-b-lg",
              )}
            >
              <div className="flex flex-1 items-center justify-between">
                <span className="text-lg font-semibold">Your order</span>
                <DisclosureButton type="button" className="flex items-center">
                  <span className="text-zinc-800">{viewText}</span>{" "}
                  <Icon icon={iconToggleType}></Icon>
                </DisclosureButton>
              </div>
              <div
                className={twMerge(
                  "flex items-end justify-between opacity-100 transition-opacity",
                  open && "hidden opacity-0",
                )}
              >
                <ItemToDisplayInTrigger orderItemList={orderItemList} />
                <div>
                  <strong>
                    {`${itemCount} ${pluralize("item", itemCount)}`}:{" "}
                  </strong>
                  <span>{formatCurrency(orderSummary.totalCharge)}</span>
                </div>
              </div>
            </div>
            <SlideTransition show={open} parentClass="duration-200 ease-out">
              <div className="relative rounded-b-lg bg-gray-100 px-4">
                <div className="flex flex-col overflow-y-auto">
                  <PromotionOrderItemList
                    promotionalOrderItems={promotionalOrderItems}
                    promotions={promotions}
                    renderOrderItems={(orderItems) => {
                      return (
                        <div className="flex flex-col divide-y divide-zinc-200">
                          {orderItems.map((orderItem) => {
                            const isProductInactive =
                              activeProductIdList.length > 0 &&
                              !activeProductIdList.includes(
                                orderItem.productId,
                              );
                            return (
                              <OrderItem
                                key={`${orderItem.productId}-${orderItem.supplierName}-${orderItem.promotionAppliedAt}-${orderItem.isCompleted}`}
                                orderItem={orderItem}
                                outOfStock={outOfStockProductIds.includes(
                                  orderItem.productId,
                                )}
                                hasPricingChanged={productIdsWithPricingChange.includes(
                                  orderItem.productId,
                                )}
                                isProductInactive={isProductInactive}
                                showSupplierName={showSupplierName}
                              />
                            );
                          })}
                        </div>
                      );
                    }}
                  />
                  {nonPromotionalOrderItems.map((orderItem) => {
                    const isProductInactive =
                      activeProductIdList.length > 0 &&
                      !activeProductIdList.includes(orderItem.productId);
                    return (
                      <OrderItem
                        key={`${orderItem.productId}-${orderItem.supplierName}-${orderItem.promotionAppliedAt}-${orderItem.isCompleted}`}
                        orderItem={orderItem}
                        outOfStock={outOfStockProductIds.includes(
                          orderItem.productId,
                        )}
                        hasPricingChanged={productIdsWithPricingChange.includes(
                          orderItem.productId,
                        )}
                        isProductInactive={isProductInactive}
                        showSupplierName={showSupplierName}
                      />
                    );
                  })}
                </div>
                <OrderSummaryPrice
                  cardContainerClassName="bg-transparent"
                  {...orderSummary}
                  delivery={formatCurrency(orderSummary.deliveryCharge)}
                />
              </div>
            </SlideTransition>
          </>
        );
      }}
    </Disclosure>
  );
};

const ItemToDisplayInTrigger = ({
  orderItemList,
}: {
  orderItemList: CheckoutOrderSummaryProps["orderItemList"];
}) => {
  if (orderItemList.length === 0) return null;
  if (orderItemList.length <= 3) {
    return (
      <div className="flex gap-2">
        {orderItemList.map((orderItem) => (
          <ImageWithFallback
            key={orderItem.title}
            img={orderItem.imageUrl}
            fallback="product-badge"
            className="h-12 w-12 rounded-full"
          />
        ))}
      </div>
    );
  }
  if (orderItemList.length > 3) {
    return (
      <div className="flex gap-2">
        {orderItemList.slice(0, 2).map((orderItem) => (
          <ImageWithFallback
            key={orderItem.title}
            img={orderItem.imageUrl}
            fallback="product-badge"
            className="h-12 w-12 rounded-lg"
          />
        ))}
        <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gray-300 bg-gray-200">{`+${orderItemList.length - 2}`}</div>
      </div>
    );
  }
};

const CheckoutOrderSummaryDesktop = ({
  orderItemList,
  orderSummary,
  outOfStockProductIds,
  productIdsWithPricingChange,
  activeProductIdList,
  promotions,
  showSupplierName,
}: CheckoutOrderSummaryProps) => {
  const itemCount = getOrderItemCount(orderItemList);
  const nonPromotionalOrderItems = orderItemList.filter(
    (orderItem) => !isPromotionalCheckoutOrderItem(orderItem),
  );
  const promotionalOrderItems = orderItemList.filter((orderItem) =>
    isPromotionalCheckoutOrderItem(orderItem),
  );

  return (
    <div className="flex h-full w-full flex-1 flex-col gap-4 bg-gray-100 p-4 font-sans text-zinc-800">
      <div className="flex justify-between">
        <span className="text-xl font-semibold">Your order</span>
        <span className="text-zinc-500">{`${itemCount} ${pluralize("item", itemCount)}`}</span>
      </div>

      <div className="flex flex-col divide-y divide-zinc-200 overflow-y-auto">
        <PromotionOrderItemList
          promotionalOrderItems={promotionalOrderItems}
          promotions={promotions}
          renderOrderItems={(orderItems) => {
            return (
              <div className="flex flex-col divide-y divide-zinc-200">
                {orderItems.map((orderItem) => {
                  const isProductInactive =
                    activeProductIdList.length > 0 &&
                    !activeProductIdList.includes(orderItem.productId);
                  return (
                    <OrderItem
                      key={`${orderItem.productId}-${orderItem.supplierName}-${orderItem.promotionAppliedAt}-${orderItem.isCompleted}`}
                      orderItem={orderItem}
                      outOfStock={outOfStockProductIds.includes(
                        orderItem.productId,
                      )}
                      hasPricingChanged={productIdsWithPricingChange.includes(
                        orderItem.productId,
                      )}
                      isProductInactive={isProductInactive}
                      showSupplierName={showSupplierName}
                    />
                  );
                })}
              </div>
            );
          }}
        />
        {nonPromotionalOrderItems.map((orderItem) => {
          const isProductInactive =
            activeProductIdList.length > 0 &&
            !activeProductIdList.includes(orderItem.productId);
          return (
            <OrderItem
              key={`${orderItem.productId}-${orderItem.supplierName}-${orderItem.promotionAppliedAt}-${orderItem.isCompleted}`}
              orderItem={orderItem}
              outOfStock={outOfStockProductIds.includes(orderItem.productId)}
              hasPricingChanged={productIdsWithPricingChange.includes(
                orderItem.productId,
              )}
              isProductInactive={isProductInactive}
              showSupplierName={showSupplierName}
            />
          );
        })}
      </div>
      <OrderSummaryPrice
        cardContainerClassName="bg-transparent"
        {...orderSummary}
        delivery={formatCurrency(orderSummary.deliveryCharge)}
      />
    </div>
  );
};

const OrderItem = ({
  orderItem,
  outOfStock,
  hasPricingChanged,
  isProductInactive,
  showSupplierName = false,
}: {
  orderItem: CheckoutOrderItem;
  outOfStock: boolean;
  hasPricingChanged: boolean;
  isProductInactive: boolean;
  showSupplierName?: boolean;
}) => {
  return (
    <div
      className={twMerge(
        "flex flex-col p-2",
        outOfStock && "bg-orange-50",
        hasPricingChanged && "bg-blue-50",
        isProductInactive && "bg-orange-50",
        orderItem.isCompleted && "opacity-50",
      )}
    >
      <div className="flex items-center justify-between gap-4 p-2">
        <div className="flex flex-1 items-center gap-4">
          {outOfStock && <Icon icon="CAUTION" color="#9a3412" />}
          <span className="font-semibold">{`${orderItem.quantity}x`}</span>
          <ImageWithFallback
            className="object-fit h-12 w-12 rounded-lg"
            img={orderItem.imageUrl}
            fallback="product-badge"
          />
          <div className="flex flex-1 flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold">
                {orderItem.title}
                {showSupplierName && orderItem.supplierName && (
                  <span className="font-normal text-zinc-500">
                    {" / "}
                    {orderItem.supplierName}
                  </span>
                )}
              </span>
              <DietaryDisplayList
                dietaryRequirements={{
                  VG: orderItem.dietaryTypes.includes("VG"),
                  GF: orderItem.dietaryTypes.includes("GF"),
                  LF: orderItem.dietaryTypes.includes("LF"),
                }}
                mode="text"
              />
            </div>
            <div className="text-zinc-500">{orderItem.description}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasPricingChanged && <Icon icon="INFORMATION" color="#1E40AF" />}
          {orderItem.isCompleted && (
            <Icon icon="CHECK" size={0.65} className="text-zinc-500" />
          )}
          <span className="font-semibold">
            {formatCurrency(orderItem.price)}
          </span>
        </div>
      </div>

      {isProductInactive && (
        <Alert
          className={"col-span-2"}
          title={"This product is no longer available"}
          message={
            "If you want to place this order you must remove the item from your basket."
          }
          type={"warning"}
        />
      )}
    </div>
  );
};

type PromotionOrderItemListProps = Pick<
  CheckoutOrderSummaryProps,
  "promotions"
> & {
  promotionalOrderItems: CheckoutOrderItem[];
  renderOrderItems: (params: CheckoutOrderItem[]) => ReactNode;
};
const PromotionOrderItemList = ({
  promotionalOrderItems,
  promotions,
  renderOrderItems,
}: PromotionOrderItemListProps) => {
  const groupedPromotionalOrderItemsByPromotionName =
    promotionalOrderItems.reduce(
      (accumulator, currentPromotionOrderItem) => {
        const promotionName =
          promotions.find((promotion) =>
            promotion.PromotionRewardItems.some(
              (promotionRewardItem) =>
                promotionRewardItem.id ===
                currentPromotionOrderItem.promotionRewardItemId,
            ),
          )?.name ?? "Promotion";
        if (!accumulator[promotionName]) {
          accumulator[promotionName] = [];
        }
        accumulator[promotionName].push(currentPromotionOrderItem);
        return accumulator;
      },
      {} as Record<string, CheckoutOrderItem[]>,
    );

  if (promotionalOrderItems.length === 0) {
    return null;
  }

  return (
    <div
      className={twMerge(
        "flex flex-col divide-y divide-purple-800 border-b border-b-purple-800",
      )}
    >
      {Object.entries(groupedPromotionalOrderItemsByPromotionName).map(
        ([promotionName, checkoutOrderItems]) => {
          return (
            <div key={promotionName} className="flex flex-col gap-2 pt-4">
              <div className="mx-2 flex">
                <div className="flex w-fit gap-2 rounded-full bg-purple-100 px-2 py-1 text-sm font-medium text-purple-800">
                  <Icon size={0.7} icon="PARTY_POPPER" />
                  <span>{promotionName}</span>
                </div>
              </div>
              {renderOrderItems(checkoutOrderItems)}
            </div>
          );
        },
      )}
    </div>
  );
};
